import React, { useState } from 'react';
import { Plus, X, Tags, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { addCategory } from '../../services/categoriesApi';
import Spinner from '../Spinner/Spinner';
import './AddCategory.css';

const AddCategory = ({ onAdded } = {}) => {
    const [category, setCategory] = useState({
        name: '',
        type: '',
        subcategories: [],
    });
    const [subcategory, setSubcategory] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleAddSubcategory = () => {
        const trimmed = subcategory.trim();
        if (!trimmed) return;
        if (trimmed.includes('-')) {
            toast.error('La subcategoría no puede contener guiones (-)');
            return;
        }
        if (category.subcategories.includes(trimmed)) {
            toast.error('Esa subcategoría ya está agregada.');
            return;
        }
        setCategory((prev) => ({
            ...prev,
            subcategories: [...prev.subcategories, trimmed],
        }));
        setSubcategory('');
    };

    const handleRemoveSubcategory = (index) => {
        setCategory((prev) => ({
            ...prev,
            subcategories: prev.subcategories.filter((_, i) => i !== index),
        }));
    };

    const handleChange = (field, value) => {
        setCategory((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const { name, type, subcategories } = category;

        if (!name || !type) {
            setError('Los campos "Nombre" y "Tipo" son obligatorios.');
            return;
        }
        if (name.includes('-')) {
            setError('El nombre de la categoría no puede contener guiones (-)');
            return;
        }
        const subsWithDash = subcategories.filter((sub) => sub.includes('-'));
        if (subsWithDash.length > 0) {
            setError(`Las siguientes subcategorías contienen guiones: ${subsWithDash.join(', ')}`);
            return;
        }

        setLoading(true);
        try {
            await addCategory(category);
            toast.success('Categoría agregada con éxito.');
            setCategory({ name: '', type: '', subcategories: [] });
            if (typeof onAdded === 'function') onAdded();
        } catch (err) {
            if (err.response?.status === 401) {
                setError('Acceso denegado.');
            } else if (
                err.response?.status === 409 ||
                err.response?.data?.error?.includes('CONFLICT') ||
                err.message?.includes('CONFLICT')
            ) {
                setError(`Ya existe una categoría llamada "${name}" para el tipo "${type}".`);
            } else {
                console.error('Error al agregar la categoría:', err);
                setError('Hubo un problema al agregar la categoría.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cme-addcat">
            <header className="cme-addcat__header">
                <div className="cme-addcat__title-icon"><Tags size={20} /></div>
                <div>
                    <h2 className="cme-addcat__title">Agregar categoría</h2>
                    <p className="cme-addcat__subtitle">
                        Creá una categoría nueva con sus subcategorías. Sin guiones en los nombres.
                    </p>
                </div>
            </header>

            {error && (
                <div className="cme-addcat__alert" role="alert">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            <form className="cme-addcat__form" onSubmit={handleSubmit}>
                {loading && <Spinner />}

                <div className="cme-addcat__row">
                    <div className="cme-addcat__field">
                        <label className="cme-addcat__label" htmlFor="cme-addcat-name">
                            Nombre <span className="cme-addcat__required">*</span>
                        </label>
                        <input
                            id="cme-addcat-name"
                            type="text"
                            className="cme-addcat__input"
                            value={category.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="Ej: Plomería"
                            required
                        />
                    </div>

                    <div className="cme-addcat__field">
                        <label className="cme-addcat__label" htmlFor="cme-addcat-type">
                            Tipo <span className="cme-addcat__required">*</span>
                        </label>
                        <select
                            id="cme-addcat-type"
                            className="cme-addcat__input"
                            value={category.type}
                            onChange={(e) => handleChange('type', e.target.value)}
                            required
                        >
                            <option value="" disabled>Seleccionar tipo</option>
                            <option value="service">Servicios</option>
                            <option value="products">Productos</option>
                        </select>
                    </div>
                </div>

                <div className="cme-addcat__field">
                    <label className="cme-addcat__label">Subcategorías</label>
                    <div className="cme-addcat__subadd">
                        <input
                            type="text"
                            className="cme-addcat__input"
                            value={subcategory}
                            onChange={(e) => setSubcategory(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddSubcategory();
                                }
                            }}
                            placeholder="Nueva subcategoría (Enter para agregar)"
                        />
                        <button
                            type="button"
                            className="cme-addcat__add-btn"
                            onClick={handleAddSubcategory}
                            disabled={!subcategory.trim()}
                        >
                            <Plus size={14} />
                            <span>Agregar</span>
                        </button>
                    </div>

                    {category.subcategories.length > 0 ? (
                        <div className="cme-addcat__chips">
                            {category.subcategories.map((sub, index) => (
                                <span key={`${sub}-${index}`} className="cme-addcat__chip">
                                    <span>{sub}</span>
                                    <button
                                        type="button"
                                        className="cme-addcat__chip-remove"
                                        onClick={() => handleRemoveSubcategory(index)}
                                        title="Quitar"
                                        aria-label={`Quitar ${sub}`}
                                    >
                                        <X size={12} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    ) : (
                        <span className="cme-addcat__hint">
                            Aún no agregaste subcategorías.
                        </span>
                    )}
                </div>

                <div className="cme-addcat__actions">
                    <button
                        type="submit"
                        className="cme-addcat__submit-btn"
                        disabled={loading}
                    >
                        <Plus size={14} />
                        <span>{loading ? 'Guardando…' : 'Crear categoría'}</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddCategory;
